import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Chapel Admin Dashboard
export function ChapelAdminDashboard({ db, functions }) {
    const [activeTab, setActiveTab] = useState('services');

    return (
        <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-2xl shadow-xl border">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#782953] mb-6">Chapel Administration</h2>

            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('services')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'services'
                                ? 'border-[#782953] text-[#782953]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Chapel Calendar
                    </button>
                    <button
                        onClick={() => setActiveTab('bulk')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'bulk'
                                ? 'border-[#782953] text-[#782953]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Add Chapel Services
                    </button>
                    <button
                        onClick={() => setActiveTab('reports')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'reports'
                                ? 'border-[#782953] text-[#782953]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Attendance Reports
                    </button>
                    <button
                        onClick={() => setActiveTab('individual')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'individual'
                                ? 'border-[#782953] text-[#782953]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Individual Reports
                    </button>
                    <button
                        onClick={() => setActiveTab('calendar-sync')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'calendar-sync'
                                ? 'border-[#782953] text-[#782953]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Google Calendar Sync
                    </button>
                </nav>
            </div>

            {activeTab === 'services' && <ChapelServicesManager db={db} />}
            {activeTab === 'bulk' && <BulkChapelServicesManager db={db} />}
            {activeTab === 'reports' && <ChapelAttendanceReports db={db} functions={functions} />}
            {activeTab === 'individual' && <IndividualAttendanceReports db={db} functions={functions} />}
            {activeTab === 'calendar-sync' && <GoogleCalendarSync db={db} />}
        </div>
    );
}

// Chapel Services Manager with Calendar Interface
function ChapelServicesManager({ db }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [dailyServices, setDailyServices] = useState({});
    const [currentMonthServices, setCurrentMonthServices] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [newService, setNewService] = useState({ name: '', time: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ message: '', isError: false });
    const monthYearFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

    // Generate calendar days for the current month
    const generateCalendarDays = useCallback(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        // Fill in days from previous month if needed
        if (firstDayOfMonth > 0) {
            const prevMonthLastDate = new Date(year, month, 0).getDate();
            const prevMonth = month === 0 ? 11 : month - 1;
            const prevMonthYear = month === 0 ? year - 1 : year;
            for (let i = prevMonthLastDate - firstDayOfMonth + 1; i <= prevMonthLastDate; i++) {
                days.push(new Date(prevMonthYear, prevMonth, i));
            }
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }

        // Fill in days from next month if needed
        const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;
        const remainingCells = totalCells - (firstDayOfMonth + daysInMonth);
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextMonthYear = month === 11 ? year + 1 : year;
        for (let i = 1; i <= remainingCells; i++) {
            days.push(new Date(nextMonthYear, nextMonth, i));
        }

        return days;
    }, [currentDate]);

    // Load daily services for the current month
    const loadMonthServices = async (monthId) => {
        try {
            const monthRef = doc(db, 'configuration', 'chapelServices', 'months', monthId);
            const monthSnap = await getDoc(monthRef);

            if (monthSnap.exists()) {
                setCurrentMonthServices(monthSnap.data().dailyServices || {});
            } else {
                setCurrentMonthServices({});
            }
        } catch (error) {
            console.error('Error loading month services:', error);
            setCurrentMonthServices({});
        }
    };    const saveDailyServices = useCallback(async (servicesToSave) => {
        const servicesToUse = servicesToSave || dailyServices;
        setIsLoading(true);
        try {
            const monthId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            const docRef = doc(db, 'configuration', 'chapelServices', 'months', monthId);
            // Use set instead of merge to properly handle deletions
            await setDoc(docRef, { dailyServices: servicesToUse });
            setStatus({ message: 'Services updated successfully', isError: false });
        } catch (error) {
            setStatus({ message: 'Error saving services', isError: true });
        } finally {
            setIsLoading(false);
        }
    }, [db, currentDate, dailyServices]);

    useEffect(() => {
        const monthId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        loadMonthServices(monthId);
        setDailyServices(currentMonthServices);
    }, [currentDate, currentMonthServices]);

    const addServiceToDate = async () => {
        if (!selectedDate || !newService.name || !newService.time) {
            setStatus({ message: 'Please select a date and provide service name and time', isError: true });
            return;
        }

        const dateString = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
        const serviceId = newService.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

        const updatedServices = { ...dailyServices };
        if (!updatedServices[dateString]) {
            updatedServices[dateString] = {};
        }

        if (updatedServices[dateString][serviceId]) {
            setStatus({ message: 'A service with this name already exists for this date', isError: true });
            return;
        }

        updatedServices[dateString][serviceId] = {
            name: newService.name,
            time: newService.time
        };

        setDailyServices(updatedServices);
        await saveDailyServices(updatedServices);
        setNewService({ name: '', time: '' });
        setStatus({ message: 'Service added successfully', isError: false });
    };

    const removeServiceFromDate = async (dateString, serviceId) => {
        if (!window.confirm('Are you sure you want to delete this service?')) return;

        const updatedServices = { ...dailyServices };
        if (updatedServices[dateString]) {
            delete updatedServices[dateString][serviceId];
            if (Object.keys(updatedServices[dateString]).length === 0) {
                delete updatedServices[dateString];
            }
        }

        setDailyServices(updatedServices);
        await saveDailyServices(updatedServices);
        setStatus({ message: 'Service deleted successfully', isError: false });
    };

    const updateServiceForDate = async (dateString, serviceId, updates) => {
        const updatedServices = {
            ...dailyServices,
            [dateString]: {
                ...dailyServices[dateString],
                [serviceId]: { ...dailyServices[dateString][serviceId], ...updates }
            }
        };
        setDailyServices(updatedServices);
        await saveDailyServices(updatedServices);
    };

    const calendarDays = generateCalendarDays();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        <div className="space-y-6">
            {/* Calendar Navigation */}
            <div className="flex justify-between items-center">
                <button
                    onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
                    className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                    &lt;
                </button>
                <h3 className="text-xl font-bold text-[#782953]">
                    Chapel Services - {monthYearFormat.format(currentDate)}
                </h3>
                <button
                    onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
                    className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                    &gt;
                </button>
            </div>

            {/* Add Service Section */}
            {selectedDate && (
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold text-[#782953] mb-4">
                        Add Service for {selectedDate.toLocaleDateString()}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <input
                            type="text"
                            placeholder="Service name"
                            value={newService.name}
                            onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                            className="p-2 border rounded-md"
                        />
                        {/* Custom Time Selector */}
                        <div className="flex gap-1">
                            <select
                                value={newService.time ? newService.time.split(':')[0] : ''}
                                onChange={(e) => {
                                    const hour = e.target.value;
                                    const minute = newService.time ? newService.time.split(':')[1] : '00';
                                    setNewService({ ...newService, time: `${hour}:${minute}` });
                                }}
                                className="p-2 border rounded-md"
                            >
                                <option value="">Hour</option>
                                {Array.from({ length: 24 }, (_, i) => (
                                    <option key={i} value={i.toString().padStart(2, '0')}>
                                        {i.toString().padStart(2, '0')}
                                    </option>
                                ))}
                            </select>
                            <span className="flex items-center">:</span>
                            <select
                                value={newService.time ? newService.time.split(':')[1] : ''}
                                onChange={(e) => {
                                    const hour = newService.time ? newService.time.split(':')[0] : '00';
                                    const minute = e.target.value;
                                    setNewService({ ...newService, time: `${hour}:${minute}` });
                                }}
                                className="p-2 border rounded-md"
                            >
                                <option value="">Min</option>
                                <option value="00">00</option>
                                <option value="15">15</option>
                                <option value="30">30</option>
                                <option value="45">45</option>
                            </select>
                        </div>
                        <button
                            onClick={addServiceToDate}
                            disabled={isLoading}
                            className="bg-[#782953] text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:bg-gray-400"
                        >
                            Add Service
                        </button>
                    </div>
                </div>
            )}

            {/* Calendar Grid */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="grid grid-cols-7 gap-0">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day =>
                        <div key={day} className="p-3 text-center font-bold text-gray-600 bg-gray-50 border-b">
                            {day}
                        </div>
                    )}

                    {calendarDays.map((date, index) => {
                        const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const dayServices = dailyServices[dateString] || {};
                        const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                        const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
                        const isPast = date < today;

                        return (
                            <div
                                key={dateString}
                                onClick={() => setSelectedDate(date)}
                                className={`min-h-[120px] p-2 border-b border-r cursor-pointer transition-colors ${
                                    !isCurrentMonth ? 'bg-gray-50 opacity-70' :
                                    isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'
                                }`}
                            >
                                <div className={`font-bold text-sm mb-2 ${
                                    isPast ? 'text-gray-400' :
                                    isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                                }`}>
                                    {date.getDate()}
                                </div>

                                <div className="space-y-1">
                                    {Object.entries(dayServices).map(([serviceId, service]) => (
                                        <div key={serviceId} className="text-xs">
                                            <div className="flex justify-between items-center bg-gray-100 rounded px-1 py-1">
                                                <span className="font-medium truncate">{service.name}</span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeServiceFromDate(dateString, serviceId);
                                                    }}
                                                    className="text-red-600 hover:text-red-800 ml-1"
                                                    title="Remove service"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            <div className="text-gray-600 px-1">
                                                {service.time}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Service Details for Selected Date */}
            {selectedDate && dailyServices[`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`] && (
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-[#782953] mb-4">
                        Services for {selectedDate.toLocaleDateString()}
                    </h4>
                    <div className="space-y-3">
                        {Object.entries(dailyServices[`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`]).map(([serviceId, service]) => (
                            <div key={serviceId} className="flex items-center gap-4 p-3 bg-gray-50 rounded">
                                <input
                                    type="text"
                                    value={service.name}
                                    onChange={(e) => updateServiceForDate(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`, serviceId, { name: e.target.value })}
                                    className="flex-1 p-2 border rounded-md"
                                />
                                {/* Custom Time Selector */}
                                <div className="flex gap-1">
                                    <select
                                        value={service.time ? service.time.split(':')[0] : ''}
                                        onChange={(e) => {
                                            const hour = e.target.value;
                                            const minute = service.time ? service.time.split(':')[1] : '00';
                                            updateServiceForDate(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`, serviceId, { time: `${hour}:${minute}` });
                                        }}
                                        className="p-2 border rounded-md"
                                    >
                                        <option value="">Hour</option>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <option key={i} value={i.toString().padStart(2, '0')}>
                                                {i.toString().padStart(2, '0')}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="flex items-center">:</span>
                                    <select
                                        value={service.time ? service.time.split(':')[1] : ''}
                                        onChange={(e) => {
                                            const hour = service.time ? service.time.split(':')[0] : '00';
                                            const minute = e.target.value;
                                            updateServiceForDate(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`, serviceId, { time: `${hour}:${minute}` });
                                        }}
                                        className="p-2 border rounded-md"
                                    >
                                        <option value="">Min</option>
                                        <option value="00">00</option>
                                        <option value="15">15</option>
                                        <option value="30">30</option>
                                        <option value="45">45</option>
                                    </select>
                                </div>
                                <button
                                    onClick={() => removeServiceFromDate(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`, serviceId)}
                                    className="text-red-600 hover:text-red-900 px-2"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {status.message && (
                <div className={`p-3 rounded-md ${status.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {status.message}
                </div>
            )}
        </div>
    );
}

// Chapel Attendance Reports
function ChapelAttendanceReports({ db, functions }) {
    const [reportType, setReportType] = useState('weekly');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const generateReport = async () => {
        setIsLoading(true);
        try {
            const getChapelReport = httpsCallable(functions, 'getChapelAttendanceReport');
            const result = await getChapelReport({
                reportType,
                startDate,
                endDate
            });
            setReportData(result.data);
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getDateRange = useCallback(() => {
        const now = new Date();

        switch (reportType) {
            case 'weekly':
                const weekStart = new Date(now);
                weekStart.setDate(now.getDate() - now.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                const weekResult = {
                    start: `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`,
                    end: `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, '0')}-${String(weekEnd.getDate()).padStart(2, '0')}`
                };
                return weekResult;
            case 'this-month':
                const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                return {
                    start: `${thisMonthStart.getFullYear()}-${String(thisMonthStart.getMonth() + 1).padStart(2, '0')}-${String(thisMonthStart.getDate()).padStart(2, '0')}`,
                    end: `${thisMonthEnd.getFullYear()}-${String(thisMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(thisMonthEnd.getDate()).padStart(2, '0')}`
                };
            case 'last-month':
                const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
                return {
                    start: `${lastMonthStart.getFullYear()}-${String(lastMonthStart.getMonth() + 1).padStart(2, '0')}-${String(lastMonthStart.getDate()).padStart(2, '0')}`,
                    end: `${lastMonthEnd.getFullYear()}-${String(lastMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(lastMonthEnd.getDate()).padStart(2, '0')}`
                };
            case 'this-semester':
                // Fall semester: July-December, Spring semester: January-June
                const isFall = now.getMonth() >= 6; // July (6) through December (11)
                const semesterStart = isFall
                    ? new Date(now.getFullYear(), 6, 1) // July 1
                    : new Date(now.getFullYear(), 0, 1); // January 1
                const semesterEnd = isFall
                    ? new Date(now.getFullYear(), 11, 31) // December 31
                    : new Date(now.getFullYear(), 5, 30); // June 30
                const semesterResult = {
                    start: `${semesterStart.getFullYear()}-${String(semesterStart.getMonth() + 1).padStart(2, '0')}-${String(semesterStart.getDate()).padStart(2, '0')}`,
                    end: `${semesterEnd.getFullYear()}-${String(semesterEnd.getMonth() + 1).padStart(2, '0')}-${String(semesterEnd.getDate()).padStart(2, '0')}`
                };
                return {
                    start: `${semesterStart.getFullYear()}-${String(semesterStart.getMonth() + 1).padStart(2, '0')}-${String(semesterStart.getDate()).padStart(2, '0')}`,
                    end: `${semesterEnd.getFullYear()}-${String(semesterEnd.getMonth() + 1).padStart(2, '0')}-${String(semesterEnd.getDate()).padStart(2, '0')}`
                };
            case 'academic-year':
                // Academic year: July 1 to June 30
                const academicYearStart = now.getMonth() >= 6
                    ? new Date(now.getFullYear(), 6, 1) // July 1 this year
                    : new Date(now.getFullYear() - 1, 6, 1); // July 1 last year
                const academicYearEnd = now.getMonth() >= 6
                    ? new Date(now.getFullYear() + 1, 5, 30) // June 30 next year
                    : new Date(now.getFullYear(), 5, 30); // June 30 this year
                return {
                    start: `${academicYearStart.getFullYear()}-${String(academicYearStart.getMonth() + 1).padStart(2, '0')}-${String(academicYearStart.getDate()).padStart(2, '0')}`,
                    end: `${academicYearEnd.getFullYear()}-${String(academicYearEnd.getMonth() + 1).padStart(2, '0')}-${String(academicYearEnd.getDate()).padStart(2, '0')}`
                };
            default:
                return { start: startDate, end: endDate };
        }
    }, [reportType, startDate, endDate]);

    useEffect(() => {
        if (reportType !== 'custom') {
            const { start, end } = getDateRange();
            setStartDate(start);
            setEndDate(end);
        }
    }, [reportType, getDateRange]);

    return (
        <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-[#782953] mb-4">Generate Attendance Report</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <select
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                        className="p-2 border rounded-md"
                    >
                        <option value="weekly">This Week</option>
                        <option value="this-month">This Month</option>
                        <option value="last-month">Last Month</option>
                        <option value="this-semester">This Semester</option>
                        <option value="academic-year">Academic Year</option>
                        <option value="custom">Custom Date Range</option>
                    </select>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            // If end date is empty, set it to the same as start date
                            if (!endDate) {
                                setEndDate(e.target.value);
                            }
                        }}
                        disabled={reportType !== 'custom'}
                        className="p-2 border rounded-md disabled:bg-gray-100"
                    />
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            // If start date is empty, set it to the same as end date
                            if (!startDate) {
                                setStartDate(e.target.value);
                            }
                        }}
                        disabled={reportType !== 'custom'}
                        className="p-2 border rounded-md disabled:bg-gray-100"
                    />
                    <button
                        onClick={generateReport}
                        disabled={isLoading || !startDate || !endDate}
                        className="bg-[#782953] text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:bg-gray-400"
                    >
                        {isLoading ? 'Generating...' : 'Generate Report'}
                    </button>
                </div>
            </div>

            {reportData && (
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="text-lg font-semibold mb-4">
                        Attendance Report: {startDate} to {endDate}
                    </h4>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                                    {reportData.services && reportData.services.map(service => (
                                        <th key={service} className="border border-gray-300 px-4 py-2 text-center">
                                            {service}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.dates && reportData.dates.map(dateData => (
                                    <tr key={dateData.date}>
                                        <td className="border border-gray-300 px-4 py-2 font-medium">
                                            {(() => {
                                                // Parse date string safely - avoid timezone conversion
                                                const [year, month, day] = dateData.date.split('-').map(Number);
                                                const localDate = new Date(year, month - 1, day);
                                                return localDate.toLocaleDateString();
                                            })()}
                                        </td>
                                        {reportData.services.map(service => {
                                            const serviceData = dateData.services[service] || {
                                                present: [], absent: [], excused: [],
                                                counts: { present: 0, absent: 0, excused: 0 }
                                            };
                                            return (
                                                <td key={service} className="border border-gray-300 px-4 py-2 text-center">
                                                    <div className="text-sm">
                                                        <div className="text-green-600">Present: {serviceData.counts?.present || 0}</div>
                                                        <div className="text-red-600">Absent: {serviceData.counts?.absent || 0}</div>
                                                        <div className="text-yellow-600">Excused: {serviceData.counts?.excused || 0}</div>
                                                        {/* Show names if there are any */}
                                                        {serviceData.present && serviceData.present.length > 0 && (
                                                            <div className="text-green-600 text-xs mt-1">Present: {serviceData.present.join(', ')}</div>
                                                        )}
                                                        {serviceData.absent && serviceData.absent.length > 0 && (
                                                            <div className="text-red-600 text-xs mt-1">Absent: {serviceData.absent.join(', ')}</div>
                                                        )}
                                                        {serviceData.excused && serviceData.excused.length > 0 && (
                                                            <div className="text-yellow-600 text-xs mt-1">Excused: {serviceData.excused.join(', ')}</div>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// Individual Attendance Reports
function IndividualAttendanceReports({ db, functions }) {
    const [selectedStudent, setSelectedStudent] = useState('');
    const [students, setStudents] = useState([]);
    const [attendanceData, setAttendanceData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dateRangeType, setDateRangeType] = useState('this-month');
    const [dateRange, setDateRange] = useState({
        start: (() => {
            const date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        })(),
        end: (() => {
            const date = new Date();
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        })()
    });

    const getIndividualDateRange = useCallback(() => {
        const now = new Date();
        switch (dateRangeType) {
            case 'this-month':
                const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                return {
                    start: `${thisMonthStart.getFullYear()}-${String(thisMonthStart.getMonth() + 1).padStart(2, '0')}-${String(thisMonthStart.getDate()).padStart(2, '0')}`,
                    end: `${thisMonthEnd.getFullYear()}-${String(thisMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(thisMonthEnd.getDate()).padStart(2, '0')}`
                };
            case 'last-month':
                const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
                return {
                    start: `${lastMonthStart.getFullYear()}-${String(lastMonthStart.getMonth() + 1).padStart(2, '0')}-${String(lastMonthStart.getDate()).padStart(2, '0')}`,
                    end: `${lastMonthEnd.getFullYear()}-${String(lastMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(lastMonthEnd.getDate()).padStart(2, '0')}`
                };
            case 'this-semester':
                // Fall semester: July-December, Spring semester: January-June
                const isFall = now.getMonth() >= 6;
                const semesterStart = isFall
                    ? new Date(now.getFullYear(), 6, 1) // July 1
                    : new Date(now.getFullYear(), 0, 1); // January 1
                const semesterEnd = isFall
                    ? new Date(now.getFullYear(), 11, 31) // December 31
                    : new Date(now.getFullYear(), 5, 30); // June 30
                return {
                    start: `${semesterStart.getFullYear()}-${String(semesterStart.getMonth() + 1).padStart(2, '0')}-${String(semesterStart.getDate()).padStart(2, '0')}`,
                    end: `${semesterEnd.getFullYear()}-${String(semesterEnd.getMonth() + 1).padStart(2, '0')}-${String(semesterEnd.getDate()).padStart(2, '0')}`
                };
            case 'academic-year':
                // Academic year: July 1 to June 30
                const academicYearStart = now.getMonth() >= 6
                    ? new Date(now.getFullYear(), 6, 1) // July 1 this year
                    : new Date(now.getFullYear() - 1, 6, 1); // July 1 last year
                const academicYearEnd = now.getMonth() >= 6
                    ? new Date(now.getFullYear() + 1, 5, 30) // June 30 next year
                    : new Date(now.getFullYear(), 5, 30); // June 30 this year
                return {
                    start: `${academicYearStart.getFullYear()}-${String(academicYearStart.getMonth() + 1).padStart(2, '0')}-${String(academicYearStart.getDate()).padStart(2, '0')}`,
                    end: `${academicYearEnd.getFullYear()}-${String(academicYearEnd.getMonth() + 1).padStart(2, '0')}-${String(academicYearEnd.getDate()).padStart(2, '0')}`
                };
            default:
                return dateRange;
        }
    }, [dateRangeType, dateRange]);

    useEffect(() => {
        if (dateRangeType !== 'custom') {
            const { start, end } = getIndividualDateRange();
            setDateRange({ start, end });
        }
    }, [dateRangeType, getIndividualDateRange]);

    const loadStudents = useCallback(async () => {
        try {
            const studentsQuery = query(
                collection(db, 'forcedRoles'),
                where('userType', 'in', ['board_paying_student', 'non_board_paying'])
            );
            const snapshot = await getDocs(studentsQuery);
            const studentList = snapshot.docs.map(doc => doc.id);
            setStudents(studentList);
        } catch (error) {
            console.error('Error loading students:', error);
        }
    }, [db]);

    useEffect(() => {
        loadStudents();
    }, [loadStudents]);

    const loadStudentAttendance = async () => {
        if (!selectedStudent) return;

        setIsLoading(true);
        try {
            const getStudentAttendance = httpsCallable(functions, 'getStudentChapelAttendance');
            const result = await getStudentAttendance({
                studentEmail: selectedStudent,
                startDate: dateRange.start,
                endDate: dateRange.end
            });
            setAttendanceData(result.data);
        } catch (error) {
            console.error('Error loading student attendance:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-[#782953] mb-4">Individual Attendance Report</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <select
                        value={selectedStudent}
                        onChange={(e) => setSelectedStudent(e.target.value)}
                        className="p-2 border rounded-md"
                    >
                        <option value="">Select a student</option>
                        {students.map(student => (
                            <option key={student} value={student}>{student}</option>
                        ))}
                    </select>
                    <select
                        value={dateRangeType}
                        onChange={(e) => setDateRangeType(e.target.value)}
                        className="p-2 border rounded-md"
                    >
                        <option value="this-month">This Month</option>
                        <option value="last-month">Last Month</option>
                        <option value="this-semester">This Semester</option>
                        <option value="academic-year">Academic Year</option>
                        <option value="custom">Custom Date Range</option>
                    </select>
                    <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => {
                            const newStart = e.target.value;
                            setDateRange(prev => ({
                                ...prev,
                                start: newStart,
                                end: !prev.end ? newStart : prev.end
                            }));
                        }}
                        disabled={dateRangeType !== 'custom'}
                        className="p-2 border rounded-md disabled:bg-gray-100"
                    />
                    <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => {
                            const newEnd = e.target.value;
                            setDateRange(prev => ({
                                ...prev,
                                end: newEnd,
                                start: !prev.start ? newEnd : prev.start
                            }));
                        }}
                        disabled={dateRangeType !== 'custom'}
                        className="p-2 border rounded-md disabled:bg-gray-100"
                    />
                    <button
                        onClick={loadStudentAttendance}
                        disabled={isLoading || !selectedStudent}
                        className="bg-[#782953] text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:bg-gray-400"
                    >
                        {isLoading ? 'Loading...' : 'Load Report'}
                    </button>
                </div>
            </div>

            {attendanceData && (
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="text-lg font-semibold mb-4">
                        Attendance for {selectedStudent}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                            <div className="text-2xl font-bold text-green-600">{attendanceData.summary.present}</div>
                            <div className="text-sm text-gray-600">Present</div>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg text-center">
                            <div className="text-2xl font-bold text-red-600">{attendanceData.summary.absent}</div>
                            <div className="text-sm text-gray-600">Absent</div>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg text-center">
                            <div className="text-2xl font-bold text-yellow-600">{attendanceData.summary.excused}</div>
                            <div className="text-sm text-gray-600">Excused</div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                                    {attendanceData.services && attendanceData.services.map(service => (
                                        <th key={service} className="border border-gray-300 px-4 py-2 text-center">
                                            {service}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {attendanceData.attendance && attendanceData.attendance.map(record => (
                                    <tr key={record.date}>
                                        <td className="border border-gray-300 px-4 py-2">
                                            {(() => {
                                                // Parse date string safely - avoid timezone conversion
                                                const [year, month, day] = record.date.split('-').map(Number);
                                                const localDate = new Date(year, month - 1, day);
                                                return localDate.toLocaleDateString();
                                            })()}
                                        </td>
                                        {attendanceData.services.map(service => {
                                            const status = record.services[service] || 'absent';
                                            const statusColors = {
                                                present: 'text-green-600 bg-green-50',
                                                absent: 'text-red-600 bg-red-50',
                                                excused: 'text-yellow-600 bg-yellow-50'
                                            };
                                            return (
                                                <td key={service} className={`border border-gray-300 px-4 py-2 text-center ${statusColors[status]}`}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// Bulk Chapel Services Manager
function BulkChapelServicesManager({ db }) {
    const [serviceName, setServiceName] = useState('');
    const [serviceTime, setServiceTime] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedDays, setSelectedDays] = useState({
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false
    });
    const [isCreating, setIsCreating] = useState(false);
    const [status, setStatus] = useState({ message: '', isError: false });

    const daysOfWeek = [
        { key: 'sunday', label: 'Sunday', value: 0 },
        { key: 'monday', label: 'Monday', value: 1 },
        { key: 'tuesday', label: 'Tuesday', value: 2 },
        { key: 'wednesday', label: 'Wednesday', value: 3 },
        { key: 'thursday', label: 'Thursday', value: 4 },
        { key: 'friday', label: 'Friday', value: 5 },
        { key: 'saturday', label: 'Saturday', value: 6 }
    ];

    const handleDayToggle = (dayKey) => {
        setSelectedDays(prev => ({
            ...prev,
            [dayKey]: !prev[dayKey]
        }));
    };

    const generateServiceId = (serviceName) => {
        return serviceName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    };

    const createBulkServices = async () => {
        if (!serviceName || !serviceTime || !startDate || !endDate) {
            setStatus({ message: 'Please fill in all required fields', isError: true });
            return;
        }

        const selectedDayValues = daysOfWeek
            .filter(day => selectedDays[day.key])
            .map(day => day.value);

        if (selectedDayValues.length === 0) {
            setStatus({ message: 'Please select at least one day of the week', isError: true });
            return;
        }

        setIsCreating(true);
        setStatus({ message: '', isError: false });

        try {
            // Parse dates as local dates to match calendar display
            const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
            const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
            const start = new Date(startYear, startMonth - 1, startDay); // month is 0-indexed
            const end = new Date(endYear, endMonth - 1, endDay);

            const serviceId = generateServiceId(serviceName);
            const serviceObj = {
                id: serviceId,
                name: serviceName,
                time: serviceTime
            };

            const monthlyUpdates = {};
            let totalServices = 0;

            // Generate dates within the range that match the selected days of week
            for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
                const dayOfWeek = currentDate.getDay();

                if (selectedDayValues.includes(dayOfWeek)) {
                    const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                    const monthId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

                    if (!monthlyUpdates[monthId]) {
                        monthlyUpdates[monthId] = {};
                    }

                    if (!monthlyUpdates[monthId][dateString]) {
                        monthlyUpdates[monthId][dateString] = {};
                    }

                    // Store as object with serviceId as key (not array)
                    monthlyUpdates[monthId][dateString][serviceId] = {
                        id: serviceId,
                        name: serviceName,
                        time: serviceTime
                    };

                    totalServices++;
                }
            }

            // Save all the monthly updates
            for (const [monthId, monthServices] of Object.entries(monthlyUpdates)) {
                // First, load existing services for this month
                const monthRef = doc(db, 'configuration', 'chapelServices', 'months', monthId);
                const monthSnap = await getDoc(monthRef);
                const existingServices = monthSnap.exists() ? monthSnap.data().dailyServices || {} : {};

                // Merge with new services (object format)
                for (const [dateString, newServices] of Object.entries(monthServices)) {
                    if (!existingServices[dateString]) {
                        existingServices[dateString] = {};
                    }

                    // Merge the service objects
                    existingServices[dateString] = {
                        ...existingServices[dateString],
                        ...newServices
                    };
                }

                // Save updated month data
                await setDoc(monthRef, { dailyServices: existingServices }, { merge: true });
            }

            const selectedDayNames = daysOfWeek
                .filter(day => selectedDays[day.key])
                .map(day => day.name)
                .join(', ');

            setStatus({
                message: `Successfully created "${serviceName}" at ${serviceTime} from ${startDate} to ${endDate} on ${selectedDayNames}. Total: ${totalServices} service instances across ${Object.keys(monthlyUpdates).length} months.`,
                isError: false
            });

            // Reset form
            setServiceName('');
            setServiceTime('');
            setStartDate('');
            setEndDate('');
            setSelectedDays({
                sunday: false,
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false
            });

        } catch (error) {
            console.error('Error creating bulk services:', error);
            setStatus({ message: 'Error creating services', isError: true });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-[#782953] mb-2">Bulk Service Creation</h3>
                <p className="text-gray-700 text-sm">
                    Create multiple chapel services at once by specifying a name, time, date range, and days of the week.
                    If a service with the same name already exists on a date, it will be overwritten.
                </p>
            </div>            <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Service Details */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Service Details</h4>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Service Name *
                            </label>
                            <input
                                type="text"
                                value={serviceName}
                                onChange={(e) => setServiceName(e.target.value)}
                                placeholder="e.g., Morning Chapel, Evening Worship"
                                className="w-full p-2 border rounded-md"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Time *
                            </label>
                            {/* Custom Time Selector */}
                            <div className="flex gap-1">
                                <select
                                    value={serviceTime ? serviceTime.split(':')[0] : ''}
                                    onChange={(e) => {
                                        const hour = e.target.value;
                                        const minute = serviceTime ? serviceTime.split(':')[1] : '00';
                                        setServiceTime(`${hour}:${minute}`);
                                    }}
                                    className="p-2 border rounded-md"
                                >
                                    <option value="">Hour</option>
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i.toString().padStart(2, '0')}>
                                            {i.toString().padStart(2, '0')}
                                        </option>
                                    ))}
                                </select>
                                <span className="flex items-center">:</span>
                                <select
                                    value={serviceTime ? serviceTime.split(':')[1] : ''}
                                    onChange={(e) => {
                                        const hour = serviceTime ? serviceTime.split(':')[0] : '00';
                                        const minute = e.target.value;
                                        setServiceTime(`${hour}:${minute}`);
                                    }}
                                    className="p-2 border rounded-md"
                                >
                                    <option value="">Min</option>
                                    <option value="00">00</option>
                                    <option value="15">15</option>
                                    <option value="30">30</option>
                                    <option value="45">45</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Date Range and Days */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Schedule</h4>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date *
                            </label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full p-2 border rounded-md"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date *
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full p-2 border rounded-md"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Days of Week *
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {daysOfWeek.map(day => (
                                    <label key={day.key} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={selectedDays[day.key]}
                                            onChange={() => handleDayToggle(day.key)}
                                            className="h-4 w-4 text-[#782953] focus:ring-[#782953] border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-900">{day.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Message */}
                {status.message && (
                    <div className={`mt-4 p-3 rounded-md ${status.isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {status.message}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        onClick={createBulkServices}
                        disabled={isCreating}
                        className="bg-[#782953] text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:bg-gray-400"
                    >
                        {isCreating ? 'Creating Services...' : 'Create Services'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Enhanced Google Calendar Bidirectional Sync Component
function GoogleCalendarSync({ db }) {
    const [calendarId, setCalendarId] = useState('svots.edu_oo3jq90j6pjag166k41onkg1rg@group.calendar.google.com');
    const [apiKey, setApiKey] = useState('AIzaSyBoyNxjpr-wBY-xnFl4ZLdGTK3hVgivbX8');
    const [isLoading, setIsLoading] = useState(false);
    const [syncStatus, setSyncStatus] = useState({ message: '', isError: false });
    const [savedConfig, setSavedConfig] = useState(null);
    const [lastSync, setLastSync] = useState(null);
    const [customEndDate, setCustomEndDate] = useState('');

    // Calendar sync state variables
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState(new Set());
    const [existingServices, setExistingServices] = useState(new Map());

    // Sync preview state
    const [showPreview, setShowPreview] = useState(false);
    const [syncPreview, setSyncPreview] = useState({
        toImport: [],
        toExport: [],
        toUpdate: [],
        toDelete: []
    });

    // Selection state for granular sync control
    const [selectedToImport, setSelectedToImport] = useState(new Set());
    const [selectedToDelete, setSelectedToDelete] = useState(new Set());

    // Tab state for sync modes: 'import' or 'import-cleanup' (removed bidirectional)
    const [syncMode, setSyncMode] = useState('import');

    // Load saved configuration
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const configDoc = await getDoc(doc(db, 'configuration', 'googleCalendar'));
                if (configDoc.exists()) {
                    const config = configDoc.data();
                    setSavedConfig(config);
                    setCalendarId(config.calendarId || 'svots.edu_oo3jq90j6pjag166k41onkg1rg@group.calendar.google.com');
                    setApiKey(config.apiKey || 'AIzaSyBoyNxjpr-wBY-xnFl4ZLdGTK3hVgivbX8');
                    setLastSync(config.lastSync ? new Date(config.lastSync.seconds * 1000) : null);
                }

                // Set default end date to 3 months from now
                const defaultEndDate = new Date();
                defaultEndDate.setMonth(defaultEndDate.getMonth() + 3);
                setCustomEndDate(`${defaultEndDate.getFullYear()}-${String(defaultEndDate.getMonth() + 1).padStart(2, '0')}-${String(defaultEndDate.getDate()).padStart(2, '0')}`);
            } catch (error) {
                console.error('Error loading calendar configuration:', error);
            }
        };
        loadConfig();
    }, [db]);

    // Load all chapel services in date range
    const loadChapelServices = async (startDate, endDate) => {
        const servicesMap = new Map();
        try {
            const monthsToCheck = new Set();
            let currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                const monthId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
                monthsToCheck.add(monthId);
                currentDate.setMonth(currentDate.getMonth() + 1);
            }

            for (const monthId of monthsToCheck) {
                const monthDoc = await getDoc(doc(db, 'configuration', 'chapelServices', 'months', monthId));
                if (monthDoc.exists()) {
                    const monthData = monthDoc.data();
                    const dailyServices = monthData.dailyServices || {};
                    for (const [dateString, dayServices] of Object.entries(dailyServices)) {
                        for (const [serviceId, service] of Object.entries(dayServices)) {
                            servicesMap.set(`${dateString}_${serviceId}`, {
                                ...service,
                                dateString,
                                serviceId,
                                monthId
                            });
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error loading chapel services:', error);
        }
        return servicesMap;
    };

    // Generate sync preview
    const generateSyncPreview = async () => {
        if (!calendarId || !apiKey || !customEndDate) {
            setSyncStatus({ message: 'Please fill in all required fields', isError: true });
            return;
        }

        setIsLoading(true);
        setSyncStatus({ message: 'Analyzing differences between calendar and chapel services...', isError: false });

        try {
            const startDate = new Date();
            const endDate = new Date(customEndDate + 'T23:59:59');

            // Load both calendar events and chapel services
            const [calendarResponse, chapelServices] = await Promise.all([
                fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?key=${apiKey}&maxResults=2500&singleEvents=true&orderBy=startTime&timeMin=${startDate.toISOString()}&timeMax=${endDate.toISOString()}`),
                loadChapelServices(startDate, endDate)
            ]);

            if (!calendarResponse.ok) {
                throw new Error(`Calendar API error: ${calendarResponse.status} ${calendarResponse.statusText}`);
            }

            const calendarData = await calendarResponse.json();
            const calendarEvents = calendarData.items || [];

            // Create maps for comparison
            const calendarMap = new Map();
            const chapelMap = new Map();

            // Process calendar events
            calendarEvents.forEach(event => {
                if (event.start?.dateTime && !event.summary?.toLowerCase().includes('no daily vespers')) {
                    const eventDateTime = new Date(event.start.dateTime);
                    const eventDate = `${eventDateTime.getFullYear()}-${String(eventDateTime.getMonth() + 1).padStart(2, '0')}-${String(eventDateTime.getDate()).padStart(2, '0')}`;
                    const key = `${eventDate}_${event.summary?.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                    calendarMap.set(key, {
                        id: event.id,
                        summary: event.summary,
                        dateTime: event.start.dateTime,
                        date: eventDate,
                        source: 'calendar'
                    });
                }
            });

            // Process chapel services
            chapelServices.forEach((service, serviceKey) => {
                const key = `${service.dateString}_${service.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                chapelMap.set(key, {
                    ...service,
                    source: 'chapel'
                });
            });

            // Find differences
            const toImport = [];
            const toExport = [];
            const toUpdate = [];
            const toDelete = [];

            if (syncMode === 'import') {
                // Import mode: only find calendar events not in chapel
                calendarMap.forEach((event, key) => {
                    if (!chapelMap.has(key)) {
                        toImport.push(event);
                    }
                });
            } else if (syncMode === 'import-cleanup') {
                // Import + cleanup mode: import missing events and mark local extras for deletion
                calendarMap.forEach((event, key) => {
                    if (!chapelMap.has(key)) {
                        toImport.push(event);
                    }
                });

                // Find chapel services that don't exist in calendar (to be deleted)
                // Only consider services from today forward for deletion to preserve past events
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                chapelMap.forEach((service, key) => {
                    if (!calendarMap.has(key)) {
                        // Parse the service date to check if it's in the past
                        const serviceDate = new Date(service.dateString + 'T00:00:00');

                        // Only mark for deletion if the service is today or in the future
                        if (serviceDate >= today) {
                            toDelete.push(service);
                        }
                    }
                });
            }

            setSyncPreview({ toImport, toExport, toUpdate, toDelete });

            // Initialize selections (import selected by default, delete unchecked by default)
            setSelectedToImport(new Set(toImport.map((_, index) => index)));
            setSelectedToDelete(new Set()); // Start with no items selected for deletion

            setShowPreview(true);
            setSyncStatus({
                message: `✅ Analysis complete: ${toImport.length} to import, ${toExport.length} to export, ${toUpdate.length} to update, ${toDelete.length} to remove`,
                isError: false
            });

        } catch (error) {
            console.error('Sync preview failed:', error);
            setSyncStatus({
                message: `❌ Failed to analyze calendar: ${error.message}`,
                isError: true
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Execute the sync based on preview
    const executeSync = async () => {
        setIsLoading(true);
        setSyncStatus({ message: `Executing ${syncMode} sync...`, isError: false });

        try {
            let importCount = 0;
            let deleteCount = 0;

            // Import selected items from calendar to chapel
            const selectedImportItems = syncPreview.toImport.filter((_, index) => selectedToImport.has(index));
            if (selectedImportItems.length > 0) {
                setSyncStatus({ message: `Importing ${selectedImportItems.length} selected events from calendar...`, isError: false });

                for (const event of selectedImportItems) {
                    const startTime = new Date(event.dateTime);
                    const timeString = startTime.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    });

                    const monthId = `${startTime.getFullYear()}-${String(startTime.getMonth() + 1).padStart(2, '0')}`;
                    const serviceId = event.summary.toLowerCase().replace(/[^a-z0-9]/g, '-');

                    const monthRef = doc(db, 'configuration', 'chapelServices', 'months', monthId);
                    const monthDoc = await getDoc(monthRef);
                    const monthData = monthDoc.exists() ? monthDoc.data() : { dailyServices: {} };

                    if (!monthData.dailyServices[event.date]) {
                        monthData.dailyServices[event.date] = {};
                    }

                    monthData.dailyServices[event.date][serviceId] = {
                        id: serviceId,
                        name: event.summary,
                        time: timeString,
                        calendarEventId: event.id
                    };

                    await setDoc(monthRef, monthData);
                    importCount++;
                }
            }

            // Delete selected local services
            const selectedDeleteItems = syncPreview.toDelete.filter((_, index) => selectedToDelete.has(index));
            if (selectedDeleteItems.length > 0) {
                setSyncStatus({ message: `Removing ${selectedDeleteItems.length} selected local services...`, isError: false });

                for (const service of selectedDeleteItems) {
                    const monthRef = doc(db, 'configuration', 'chapelServices', 'months', service.monthId);
                    const monthDoc = await getDoc(monthRef);

                    if (monthDoc.exists()) {
                        const monthData = monthDoc.data();
                        const dailyServices = monthData.dailyServices || {};

                        if (dailyServices[service.dateString] && dailyServices[service.dateString][service.serviceId]) {
                            delete dailyServices[service.dateString][service.serviceId];

                            // If no services left for this date, remove the date entry
                            if (Object.keys(dailyServices[service.dateString]).length === 0) {
                                delete dailyServices[service.dateString];
                            }

                            await setDoc(monthRef, { dailyServices });
                            deleteCount++;
                        }
                    }
                }
            }

            // Save sync configuration and timestamp
            const configRef = doc(db, 'configuration', 'googleCalendar');
            await setDoc(configRef, {
                calendarId,
                apiKey,
                lastSync: new Date(),
                syncMode
            });

            setSyncStatus({
                message: `✅ Sync completed! Imported ${importCount} events${deleteCount > 0 ? `, removed ${deleteCount} local services` : ''}.`,
                isError: false
            });

            setShowPreview(false);
            setLastSync(new Date());

        } catch (error) {
            console.error('Sync execution failed:', error);
            setSyncStatus({
                message: `❌ Sync failed: ${error.message}`,
                isError: true
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white border rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-[#782953]">📅 Google Calendar Sync</h3>

                {/* Sync Mode Selection */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sync Mode</label>
                    <div className="flex flex-col gap-3">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="import"
                                checked={syncMode === 'import'}
                                onChange={(e) => setSyncMode(e.target.value)}
                                className="mr-2"
                            />
                            <span>Import Only (Calendar → Chapel)</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="import-cleanup"
                                checked={syncMode === 'import-cleanup'}
                                onChange={(e) => setSyncMode(e.target.value)}
                                className="mr-2"
                            />
                            <span>Import + Cleanup (Calendar → Chapel, remove local extras)</span>
                        </label>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                        {syncMode === 'import'
                            ? 'Only imports events from Google Calendar to chapel services'
                            : 'Imports from calendar and removes local services not found in calendar'
                        }
                    </p>
                </div>

                {/* Information Note */}
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                        <div className="text-blue-600">ℹ️</div>
                        <div className="text-sm text-blue-800">
                            <strong>Note:</strong> This sync is one-way only. The system imports from Google Calendar to Chapel services but never modifies your Google Calendar. Your calendar remains untouched.
                        </div>
                    </div>
                </div>

                {/* Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Google Calendar ID</label>
                        <input
                            type="text"
                            value={calendarId}
                            onChange={(e) => setCalendarId(e.target.value)}
                            className="w-full p-2 border rounded-md text-sm"
                            placeholder="your-calendar@group.calendar.google.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Google API Key</label>
                        <input
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            className="w-full p-2 border rounded-md text-sm"
                            placeholder="Your Google Calendar API Key"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sync Until Date</label>
                    <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full md:w-auto p-2 border rounded-md text-sm"
                    />
                </div>

                {/* Status Display */}
                {syncStatus.message && (
                    <div className={`p-3 rounded-md mb-4 ${syncStatus.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                        {syncStatus.message}
                    </div>
                )}

                {/* Last Sync Info */}
                {lastSync && (
                    <div className="text-sm text-gray-600 mb-4">
                        Last synced: {lastSync.toLocaleString()}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={generateSyncPreview}
                        disabled={isLoading}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Analyzing...' : `Preview ${syncMode === 'import' ? 'Import' : 'Import + Cleanup'}`}
                    </button>
                </div>
            </div>

            {/* Sync Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-xl font-bold mb-4 text-[#782953]">
                                🔍 Sync Preview - {syncMode === 'import' ? 'Import Only' : 'Import + Cleanup'}
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Import Preview with Checkboxes */}
                                {syncPreview.toImport.length > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="font-bold text-green-800">
                                                📥 Import to Chapel ({selectedToImport.size}/{syncPreview.toImport.length})
                                            </h4>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setSelectedToImport(new Set(syncPreview.toImport.map((_, i) => i)))}
                                                    className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
                                                >
                                                    Select All
                                                </button>
                                                <button
                                                    onClick={() => setSelectedToImport(new Set())}
                                                    className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
                                                >
                                                    Select None
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {syncPreview.toImport.map((event, index) => (
                                                <div key={index} className="flex items-start gap-2 text-sm text-green-700 bg-white p-2 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedToImport.has(index)}
                                                        onChange={(e) => {
                                                            const newSet = new Set(selectedToImport);
                                                            if (e.target.checked) {
                                                                newSet.add(index);
                                                            } else {
                                                                newSet.delete(index);
                                                            }
                                                            setSelectedToImport(newSet);
                                                        }}
                                                        className="mt-1 flex-shrink-0"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium">{event.summary}</div>
                                                        <div className="text-xs opacity-75">
                                                            {new Date(event.dateTime).toLocaleDateString()} at {new Date(event.dateTime).toLocaleTimeString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Export Preview */}
                                {syncPreview.toExport.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h4 className="font-bold text-blue-800 mb-2">
                                            📤 Export to Calendar ({syncPreview.toExport.length})
                                        </h4>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {syncPreview.toExport.map((service, index) => (
                                                <div key={index} className="text-sm text-blue-700 bg-white p-2 rounded">
                                                    <div className="font-medium">{service.name}</div>
                                                    <div className="text-xs opacity-75">
                                                        {service.dateString} at {service.time}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-2 text-xs text-blue-600">
                                            ⚠️ Requires OAuth authentication for calendar write access
                                        </div>
                                    </div>
                                )}

                                {/* Delete Preview with Checkboxes */}
                                {syncPreview.toDelete.length > 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="font-bold text-red-800">
                                                🗑️ Remove from Chapel ({selectedToDelete.size}/{syncPreview.toDelete.length})
                                            </h4>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setSelectedToDelete(new Set(syncPreview.toDelete.map((_, i) => i)))}
                                                    className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                                                >
                                                    Select All
                                                </button>
                                                <button
                                                    onClick={() => setSelectedToDelete(new Set())}
                                                    className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
                                                >
                                                    Select None
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {syncPreview.toDelete.map((service, index) => (
                                                <div key={index} className="flex items-start gap-2 text-sm text-red-700 bg-white p-2 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedToDelete.has(index)}
                                                        onChange={(e) => {
                                                            const newSet = new Set(selectedToDelete);
                                                            if (e.target.checked) {
                                                                newSet.add(index);
                                                            } else {
                                                                newSet.delete(index);
                                                            }
                                                            setSelectedToDelete(newSet);
                                                        }}
                                                        className="mt-1 flex-shrink-0"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium">{service.name}</div>
                                                        <div className="text-xs opacity-75">
                                                            {service.dateString} at {service.time}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-2 text-xs text-red-600">
                                            {syncMode === 'import-cleanup'
                                                ? 'Local services not found in Google Calendar'
                                                : 'Services removed from Google Calendar'
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* No changes */}
                            {syncPreview.toImport.length === 0 && syncPreview.toDelete.length === 0 && (
                                <div className="text-center py-8">
                                    <div className="text-4xl mb-4">✅</div>
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">Everything is in sync!</h4>
                                    <p className="text-gray-600">No changes needed between calendar and chapel services.</p>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowPreview(false)}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                {(selectedToImport.size > 0 || selectedToDelete.size > 0) && (
                                    <button
                                        onClick={executeSync}
                                        disabled={isLoading}
                                        className="px-4 py-2 bg-[#782953] text-white rounded-md hover:bg-opacity-90 disabled:opacity-50"
                                    >
                                        {isLoading ? 'Syncing...' : `Execute Sync (${selectedToImport.size + selectedToDelete.size} items)`}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Setup Instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-yellow-800 mb-2">📋 Setup Instructions</h4>
                <ol className="text-sm text-yellow-700 space-y-2 list-decimal list-inside">
                    <li>Go to the <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                    <li>Create a new project or select an existing one</li>
                    <li>Enable the Google Calendar API</li>
                    <li>Create credentials (API Key) for the Calendar API</li>
                    <li>Copy the API key and paste it above</li>
                    <li>Find your Google Calendar ID in Calendar Settings → Integrate calendar</li>
                    <li>For bidirectional sync, OAuth setup will be required for calendar write access</li>
                </ol>
                <p className="text-sm text-yellow-600 mt-2">
                    <strong>Note:</strong> Import mode only requires read access. Bidirectional sync needs write permissions.
                </p>
            </div>
        </div>
    );
}
